/**
 * Budget Status Display Helpers
 *
 * Shared UI mapping from a budget usage percentage to a Bootstrap color
 * variant and a Spanish status label. Used by every place that renders a
 * budget usage badge/progress bar (BudgetOverviewWidget, ProjectsTable) so
 * the thresholds and copy never drift apart (#276, #325, #406). Do not
 * reimplement these thresholds elsewhere — import from here instead.
 *
 * @module utils/budgetStatus
 */

import { BUDGET_THRESHOLD } from './constants';

/**
 * #765 — LOS UMBRALES, RE-EXPORTADOS DESDE EL ÚNICO LUGAR QUE YA LOS TENÍA.
 *
 * `src/utils/constants.js` YA declaraba `BUDGET_THRESHOLD = { WARNING: 80,
 * CRITICAL: 100 }` — no hacía falta un valor nuevo, hacía falta que el resto
 * del árbol lo usara. Medido buscando `>= 80` / `>= 100` en contexto de
 * presupuesto en todo `src/`: SIETE sitios tenían el literal a mano y NINGUNO
 * importaba `BUDGET_THRESHOLD` — `src/services/projectService.js` (el status
 * Y el default del parámetro `threshold` de `getProjectsWithBudgetAlerts`),
 * `src/utils/calculations.js`, acá mismo (×2, antes de este cambio),
 * `src/pages/expenses/ExpenseForm.js` (×2) y
 * `src/pages/projects/ProjectDetail.js` (×3). El único consumidor que ya lo
 * hacía bien era `src/components/common/BudgetAlertBanner.js`.
 *
 * Estos dos nombres (`BUDGET_WARNING_THRESHOLD` / `BUDGET_CRITICAL_THRESHOLD`)
 * son el nombre plano que usa el resto de este módulo y sus consumidores —
 * mismo par de nombres que su gemelo `mcp-server/src/services/calculations.js`
 * (`BUDGET_WARNING_THRESHOLD` / `BUDGET_CRITICAL_THRESHOLD`), DOS árboles y no
 * uno porque CRA sólo compila lo que cuelga de `src/` (misma razón por la que
 * `testDataScope.js` existe dos veces). El VALOR sale de `constants.js`, no se
 * repite acá.
 *
 * `mcp-server/tests/services/budgetThresholdLaneParity.test.js` compara el
 * valor de estas dos constantes contra las del carril MCP y exige que ningún
 * consumidor de la lista de arriba tenga un literal `>= 80` / `>= 100` propio
 * en contexto de presupuesto — el mismo movimiento que
 * `testDataMarkingLaneParity.test.js` ya hace para `is_test_data`.
 *
 * `check_project_budget_status` en la base usa los mismos números (verificado
 * con `pg_get_functiondef` en local) pero no está gateada por ese test — no
 * hay forma de importar SQL en un test de JS.
 */
export const BUDGET_WARNING_THRESHOLD = BUDGET_THRESHOLD.WARNING;
export const BUDGET_CRITICAL_THRESHOLD = BUDGET_THRESHOLD.CRITICAL;

/**
 * El veredicto (no la presentación) para un porcentaje consumido, en el MISMO
 * vocabulario que `calculateBudgetMetrics` (`src/services/projectService.js`)
 * y `calculateBudgetVariance` (`src/utils/calculations.js`, y su gemelo MCP):
 * `no_budget` / `ok` / `warning` / `critical`.
 *
 * `null`/`undefined` es "no calculable", NO "0% consumido" — ver #679/#685
 * más abajo. Los llamadores que ya resuelven `no_budget` con su propia
 * compuerta (sin denominador) siguen haciéndolo antes de llegar acá; esta
 * función asume que YA hay un porcentaje real que comparar.
 *
 * @param {number|null|undefined} percentageUsed
 * @returns {'ok'|'warning'|'critical'|'no_budget'}
 */
export const budgetStatusFromPercentage = (percentageUsed) => {
  if (percentageUsed === null || percentageUsed === undefined) return 'no_budget';
  if (percentageUsed >= BUDGET_CRITICAL_THRESHOLD) return 'critical';
  if (percentageUsed >= BUDGET_WARNING_THRESHOLD) return 'warning';
  return 'ok';
};

/**
 * Get the Bootstrap color variant for a given budget usage percentage.
 *
 * #679/#685 — `null` (no calculable) NO es verde. `calculateBudgetMetrics`
 * devuelve `percentageUsed: null` cuando el proyecto no tiene monto cotizado,
 * y sin este caso explícito ese `null` caía en el `return 'success'` de abajo:
 * un proyecto sin presupuesto se pintaba "Normal" en verde, que es el mismo
 * engaño que el `status: 'OK'` de las tools MCP.
 *
 * @param {number|null|undefined} percentageUsed
 * @returns {'success'|'warning'|'danger'|'secondary'}
 */
export const getBudgetColorVariant = (percentageUsed) => {
  if (percentageUsed === null || percentageUsed === undefined) return 'secondary';
  if (percentageUsed >= BUDGET_CRITICAL_THRESHOLD) return 'danger';
  if (percentageUsed >= BUDGET_WARNING_THRESHOLD) return 'warning';
  return 'success';
};

/**
 * Get the Spanish status label for a given budget usage percentage.
 *
 * @param {number|null|undefined} percentageUsed
 * @returns {'Normal'|'Alerta'|'Excedido'|'Sin presupuesto'}
 */
export const getBudgetStatusLabel = (percentageUsed) => {
  if (percentageUsed === null || percentageUsed === undefined) return 'Sin presupuesto';
  if (percentageUsed >= BUDGET_CRITICAL_THRESHOLD) return 'Excedido';
  if (percentageUsed >= BUDGET_WARNING_THRESHOLD) return 'Alerta';
  return 'Normal';
};

const budgetStatus = {
  BUDGET_WARNING_THRESHOLD,
  BUDGET_CRITICAL_THRESHOLD,
  budgetStatusFromPercentage,
  getBudgetColorVariant,
  getBudgetStatusLabel,
};

export default budgetStatus;
